// For localised charter labels in React components, use useCharterFlightLabels (hooks/Flights/useCharterFlightLabels).
import type { MessageDescriptor } from 'react-intl'
import { defineMessage, defineMessages } from 'react-intl'
import config from './config'
import { FlightsClassTypes } from './flight'

export const CHARTER_BUNDLE_BADGE_MESSAGE = defineMessage({
  id: 'RZ1GSHEJ1CCmodPXtmMk',
  defaultMessage: 'Direct overnight flights & bundled hotel stays',
  description: 'Badge text for Maldives charter bundle in the flight search or booking flow',
})

export const SUNDAY = 0
export const MONDAY = 1
export const TUESDAY = 2
export const WEDNESDAY = 3
export const THURSDAY = 4
export const FRIDAY = 5
export const SATURDAY = 6

export const MALDIVES_CHARTER_PRIORITY_SUPPORT_NUMBER_HREF = `tel:${config.MALDIVES_CHARTER_PRIORITY_SUPPORT_NUMBER || ''.replace(/\s+/g, '')}`

export const CHARTER_PAGE_SLUG = 'maldives-direct'
export const CHARTER_BEFORE_YOU_FLY_PAGE_SLUG = 'maldives-charter-before-you-fly'

// Maldives location identifiers
export const MALDIVES_COUNTRY_CODE = 'MV'
export const MALDIVES_COUNTRY_NAME = 'maldives'

// make as a fallback price for the maldives bundle
export const MALDIVES_BUNDLE_DEFAULT_PRICE_PER_PERSON = 2499

export const MELBOURNE_CHARTER_EXCLUDED_AIRPORT_CODES = ['MELALL', 'AVV'] as const

export const MALDIVES_BUNDLE_DEFAULT_GUESTS = 2

export const MALDIVES_BUNDLE_DEFAULT_NIGHTS = 5

export const MAX_TRAVELLERS_FOR_MALDIVES_CHARTER = 9

export const MALDIVES_BUNDLE_LABEL = 'Hotel & Flight bundle'
export const MALDIVES_CAMPAIGN_NAME = 'Maldives Direct Banner (Maldives Hotel Search)'

export const MALDIVES_MARKETING_BANNER_IMAGE_ID = 'maldives-charter-landing-banner'

// Earliest charter departure (local date)
export const MALDIVES_CHARTER_START_TARGET_DATE = '2026-05-18' // need to be updated to the actual start date
export const MALDIVES_CHARTER_END_TARGET_DATE = '2035-01-01' // can be extend as it does not affect data fetch

// For search page, if user check-in date is outside range, we will not perform bundle data fetch
export const MALDIVES_CHARTER_SEARCH_PAGE_MIN_CHECK_IN_DATE = '2026-05-01'
export const MALDIVES_CHARTER_SEARCH_PAGE_MAX_CHECK_IN_DATE = '2026-10-31'

export const CHARTER_BUNDLE_DURATION_OPTIONS = [
  { value: 5, label: '5 nights & flights' },
  { value: 12, label: '12 nights & flights' },
] as const

/**
 * Maps bundle nights to total trip duration days
 * - 5 nights bundle = 7 days total trip (departure to return)
 * - 12 nights bundle = 14 days total trip (departure to return)
 */
export const CHARTER_NIGHTS_TO_DURATION_DAYS = {
  5: 7,
  12: 14,
} as const

export type CharterBundleNights = keyof typeof CHARTER_NIGHTS_TO_DURATION_DAYS

export const HOTEL_OFFERS_PAGE_SIZE = 20
export const HOTEL_OFFERS_LIMIT = 20
export const CHARTER_CALENDAR_PAGE_SIZE = 6
// Inventory display thresholds
export const MAX_SEATS_LEFT_TO_SHOW_INVENTORY = 10
export const MAX_FNF_SEATS_LEFT_TO_SHOW_INVENTORY = 10
export const MAX_ROOMS_LEFT_TO_SHOW_INVENTORY = 5

// Maldives Charter Flight Configuration
export const MALDIVES_CHARTER_CONFIG = {
  DEPARTURE_WEEKDAY: 1, // Monday
  FUTURE_MONTHS_AMOUNT: 7,
  DEPARTURE_AIRPORT: 'MEL',
  ARRIVAL_AIRPORT: 'MLE',
} as const

const melbourneAirport: App.AirportLocation = {
  'id': 3402,
  'cityAirportName': '',
  'cityCode': 'MEL',
  'cityDesc': 'Melbourne',
  'airportCode': 'MEL',
  'airportName': 'Tullamarine',
  'country': 'Australia',
  'isMultiAirport': false,
  'isChildDisplay': true,
}

const maleAirport: App.AirportLocation = {
  'id': 2264,
  'cityAirportName': '',
  'cityCode': 'MLE',
  'cityDesc': 'Malé',
  'airportCode': 'MLE',
  'airportName': 'Malé Intl',
  'country': 'Maldives',
  'isMultiAirport': false,
  'isChildDisplay': false,
  'lt': 4.19183016,
  'lg': 73.52909851,
  'isCityAirport': false,
}

const sydneyAirport: App.AirportLocation = {
  'id': 3008,
  'cityAirportName': '',
  'cityCode': 'SYD',
  'cityDesc': 'Sydney',
  'airportCode': 'SYD',
  'airportName': 'Sydney',
  'country': 'Australia',
  'isMultiAirport': false,
  'isChildDisplay': false,
}

const brisbaneAirport: App.AirportLocation = {
  'id': 3005,
  'cityAirportName': '',
  'cityCode': 'BNE',
  'cityDesc': 'Brisbane',
  'airportCode': 'BNE',
  'airportName': 'Brisbane',
  'country': 'Australia',
  'isMultiAirport': false,
  'isChildDisplay': false,
}

const adelaideAirport: App.AirportLocation = {
  'id': 2885,
  'cityAirportName': '',
  'cityCode': 'ADL',
  'cityDesc': 'Adelaide',
  'airportCode': 'ADL',
  'airportName': 'Adelaide',
  'country': 'Australia',
  'isMultiAirport': false,
  'isChildDisplay': false,
}

export const maldivianTailImage: App.Image = {
  id: 'maldivianTailImage',
}

interface CharterFlightDetails {
  /** The airport name as we want to show it in the UI, which doesn't necessarily match what's in the airport data */
  originAirportName: string
  originAirport: App.AirportLocation
  destinationAirport: App.AirportLocation
  feederFlightOrigins: Array<App.AirportLocation>
  fareClassOptions: Array<{
    value: FlightsClassTypes
    label: string
  }>
  operatorName: string
  operatorLogo: App.Image
  departureDays: Array<number>
  returnDays: Array<number>
  validDurations: Array<number>
  supportNumber: {
    humanReadable: string
    href: string
  }
  dateRange: {
    min: string
    max: string
  }
}

export const CHARTER_FLIGHT_DETAILS: { [route in App.CharterBundleRoute]: CharterFlightDetails } = {
  'melbourne-maldives': {
    originAirportName: 'Melbourne Airport',
    originAirport: melbourneAirport,
    destinationAirport: maleAirport,
    feederFlightOrigins: [
      sydneyAirport,
      brisbaneAirport,
      adelaideAirport,
    ],
    fareClassOptions: [
      { value: FlightsClassTypes.ECONOMY, label: 'Economy' },
      { value: FlightsClassTypes.PREMIUM, label: 'Economy Plus' },
      { value: FlightsClassTypes.LUXROW, label: 'SkyLounge' },
      { value: FlightsClassTypes.BUSINESS, label: 'Business' },
    ],
    operatorName: 'Maldivian',
    operatorLogo: maldivianTailImage,
    departureDays: [MONDAY],
    returnDays: [MONDAY],
    validDurations: [5, 12],
    supportNumber: {
      humanReadable: config.MALDIVES_CHARTER_PRIORITY_SUPPORT_NUMBER,
      href: MALDIVES_CHARTER_PRIORITY_SUPPORT_NUMBER_HREF,
    },
    dateRange: {
      min: MALDIVES_CHARTER_START_TARGET_DATE,
      max: MALDIVES_CHARTER_END_TARGET_DATE,
    },
  },
}

export const MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG = 'maldives_direct_flight_bundle' as const
export const MALDIVES_CHARTER_CAMPAIGN_TAG = 'maldivescharter' as const
export const MALDIVES_GROUP_VILLA_CHARTER_CAMPAIGN_TAG = 'Maldives Group Villa Charter' as const
export const KIDS_FLY_FREE_CAMPAIGN_TAG = 'Kids Fly Free' as const

export const MALDIVES_CHARTER_FARE_TAG_ECONOMY = 'maldives_direct_fare_economy' as const
export const MALDIVES_CHARTER_FARE_TAG_ECONOMY_PLUS = 'maldives_direct_fare_economy_plus' as const
export const MALDIVES_CHARTER_FARE_TAG_SKYLOUNGE = 'maldives_direct_fare_skylounge' as const
export const MALDIVES_CHARTER_FARE_TAG_BUSINESS = 'maldives_direct_fare_business' as const

export const NON_CHARTER_TO_CHARTER_OFFER_MAP: Record<string, string> = {
  '006RF00000JWTvCYAX': '006RF00000Ol82jYAB',
  '006RF00000MK38LYAT': '006RF00000OnBB4YAN',
  '006RF00000NGpu9YAD': '006RF00000OXJu5YAH',
  '006RF00000HALMNYA5': '006RF00000NVMgXYAX',
  '006RF00000NJNEzYAP': '006RF00000NZkFfYAL',
}

export const CHARTER_BUNDLE_BANNER_CAMPAIGN_NAME = 'Charter Bundle Banner (Non-Charter Offer Page)'

export const KIDS_FLY_FREE_COLOUR = '#0E839C'

export const MALDIVES_DREAM_TILE_TITLE_MESSAGES: Record<string, MessageDescriptor> = defineMessages({
  'the-maldives-best-all-inclusive-resorts': {
    id: '67P7-QfY9aZiN9VIv6I3',
    defaultMessage: 'The Maldives Best All-Inclusive Resorts',
    description: 'Title for an inspiration article card shown in the Maldives dream block on the Maldives Direct search page',
  },
  'maldives-your-way-from-no-expense-spared-to-under-$5k': {
    id: 'xRn92yPx58qiSVvh8Hqg',
    defaultMessage: 'Maldives Your Way: From No Expense Spared to Under $5K',
    description: 'Title for an inspiration article card shown in the Maldives dream block on the Maldives Direct search page',
  },
  'best-family-resorts-in-the-maldives': {
    id: 'CHRFLsefVJbU5EhTQm5P',
    defaultMessage: 'Best Family Resorts in the Maldives',
    description: 'Title for an inspiration article card shown in the Maldives dream block on the Maldives Direct search page',
  },
  'maldives-direct-flight': {
    id: 'xm5fkot8DwyWokClM-4T',
    defaultMessage: 'Maldives Direct Flight',
    description: 'Title for an inspiration article card shown in the Maldives dream block on the Maldives Direct search page',
  },
  'how-to-get-to-the-maldives': {
    id: 'tmNIrBroSg-QD4QqIqRt',
    defaultMessage: 'How to Get to the Maldives',
    description: 'Title for an inspiration article card shown in the Maldives dream block on the Maldives Direct search page',
  },
  'a-guide-to-the-atolls-of-the-maldives': {
    id: 'TRprLEaY6X7-TRawHpek',
    defaultMessage: 'A Guide to the Atolls of the Maldives',
    description: 'Title for an inspiration article card shown in the Maldives dream block on the Maldives Direct search page',
  },
  'the-most-luxurious-resorts-in-the-maldives': {
    id: 'E-XcKjl8119BnuQzDyBi',
    defaultMessage: 'The Most Luxurious Resorts in the Maldives',
    description: 'Title for an inspiration article card shown in the Maldives dream block on the Maldives Direct search page',
  },
  'maldives-best-honeymoon-resorts': {
    id: '-XA_dTDHlkbV3W_EaUlR',
    defaultMessage: 'Maldives Best Honeymoon Resorts',
    description: 'Title for an inspiration article card shown in the Maldives dream block on the Maldives Direct search page',
  },
  'pullman-maldives-maamutaa-what-to-expect': {
    id: 'txdisNKnC-AUqRa_kdQg',
    defaultMessage: 'Pullman Maldives Maamutaa: What to Expect',
    description: 'Title for an inspiration article card shown in the Maldives dream block on the Maldives Direct search page',
  },
  '5-things-we-love-about-hard-rock-hotel-maldives': {
    id: 'obe4YT_VTZHeu9SwlLVQ',
    defaultMessage: '5 Things We Love About Hard Rock Hotel Maldives',
    description: 'Title for an inspiration article card shown in the Maldives dream block on the Maldives Direct search page',
  },
  'six-surprising-things-about-ja-manafaru-maldives': {
    id: 'l-pvtT6H8G4duuo4iL_2',
    defaultMessage: 'Six Surprising Things About JA Manafaru Maldives',
    description: 'Title for an inspiration article card shown in the Maldives dream block on the Maldives Direct search page',
  },
})

export const MALDIVES_DREAM_TILES: Array<Omit<App.HubTile, 'title' | 'linkText'> & {
  image: keyof typeof MALDIVES_DREAM_TILE_TITLE_MESSAGES
}> = [
  {
    filterLabel: '',
    image: 'the-maldives-best-all-inclusive-resorts',
    show: true,
    hrefLink: 'https://luxuryescapes.com/inspiration/maldives-best-all-inclusive-resorts/',
  },
  {
    filterLabel: '',
    image: 'maldives-your-way-from-no-expense-spared-to-under-$5k',
    show: true,
    hrefLink: 'https://luxuryescapes.com/inspiration/maldives-your-way-from-no-expense-spared-to-under-5k/',
  },
  {
    filterLabel: '',
    image: 'best-family-resorts-in-the-maldives',
    show: true,
    hrefLink: 'https://luxuryescapes.com/inspiration/treat-the-kids-with-the-best-family-resorts-in-the-maldives/',
  },
  {
    filterLabel: '',
    image: 'maldives-direct-flight',
    show: true,
    hrefLink: 'https://luxuryescapes.com/inspiration/maldives-direct-flight/',
  },
  {
    filterLabel: '',
    image: 'how-to-get-to-the-maldives',
    show: true,
    hrefLink: 'https://luxuryescapes.com/inspiration/paradise-is-open-heres-how-to-get-to-the-maldives/',
  },
  {
    filterLabel: '',
    image: 'a-guide-to-the-atolls-of-the-maldives',
    show: true,
    hrefLink: 'https://luxuryescapes.com/inspiration/a-guide-to-the-atolls-of-the-maldives/',
  },
  {
    filterLabel: '',
    image: 'the-most-luxurious-resorts-in-the-maldives',
    show: true,
    hrefLink: 'https://luxuryescapes.com/inspiration/heaven-on-earth-the-most-luxurious-resorts-in-the-maldives/',
  },
  {
    filterLabel: '',
    image: 'maldives-best-honeymoon-resorts',
    show: true,
    hrefLink: 'https://luxuryescapes.com/inspiration/maldives-best-honeymoon-resorts/',
  },
  {
    filterLabel: '',
    image: 'pullman-maldives-maamutaa-what-to-expect',
    show: true,
    hrefLink: 'https://luxuryescapes.com/inspiration/pullman-maldives-maamutaa-what-to-expect/',
  },
  {
    filterLabel: '',
    image: '5-things-we-love-about-hard-rock-hotel-maldives',
    show: true,
    hrefLink: 'https://luxuryescapes.com/inspiration/5-things-we-love-about-hard-rock-hotel-maldives/',
  },
  {
    filterLabel: '',
    image: 'six-surprising-things-about-ja-manafaru-maldives',
    show: true,
    hrefLink: 'https://luxuryescapes.com/inspiration/six-surprising-things-about-a-villa-stay-at-ja-manafaru-maldives/',
  },
]
